import { InputModalityDetector } from '@angular/cdk/a11y';
import {
  Overlay,
  OVERLAY_DEFAULT_CONFIG,
  OverlayContainer,
  OverlayDefaultConfig,
  OverlayModule,
} from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeRo from '@angular/common/locales/ro';
import { CUSTOM_ELEMENTS_SCHEMA, DoBootstrap, inject, LOCALE_ID, NgModule, provideAppInitializer } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { UpgradeModule } from '@angular/upgrade/static';

import { MsalBroadcastService, MsalInterceptor, MsalModule, MsalRedirectComponent } from '@azure/msal-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateLoader } from '@ngx-translate/core';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { ToastrModule } from 'ngx-toastr';

import { TitleStrategy } from '@angular/router';
import { A11yColorFilterComponent } from '@shared/components/a11y/a11y-color-filter.component';
import { A11yReadingAidComponent } from '@shared/components/a11y/a11y-reading-aid.component';
import { AuthInterceptor, LocaleDateAdapter, LocaleService } from '@shared/services';
import { AppCdkOverlayContainer, AppCdkViewportRuler } from '@shared/services/app-cdk-config.service';
import { CipoTitleStrategy } from '@shared/services/cipo-title.strategy';
import { EnvironmentConfigService } from '@shared/services/environment-config.service';
import { TranslationApiLoader } from '@shared/services/translation-api-loader.service';
import { TranslationService } from '@shared/services/translation.service';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from '@shared/services/msal-factory';
import { LayoutComponent } from '@system/layout/layout.component';
import { LoadingComponent } from '@system/loading/loading.component';
import { CipoAiContainerComponent } from '@system/search/cipo-ai/cipo-ai.component';
import { SmartBannerComponent } from '@system/smart-banner/smart-banner.component';
import { provideHighcharts } from 'highcharts-angular';
import { provideMarkdown } from 'ngx-markdown';
import { CustomBreakPointsModule } from 'src/app-breakpoints.module';
import { AppRoutingModule } from 'src/app/app-routing.module';
import { AppComponent } from 'src/app/app.component';

registerLocaleData(localeRo);

@NgModule({
  // TODO: check if this is needed (this allows unknown elements in templates, smthing like 'any' for html :))
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserAnimationsModule,
    UpgradeModule,
    BrowserModule,
    LayoutComponent,
    LoadingComponent,
    SmartBannerComponent,
    CipoAiContainerComponent,
    MatSidenavModule,
    OverlayModule,
    PortalModule,
    CustomBreakPointsModule,
    A11yColorFilterComponent,
    A11yReadingAidComponent,
    MsalModule.forRoot(MSALInstanceFactory(), MSALGuardConfigFactory(), MSALInterceptorConfigFactory()),
    ToastrModule.forRoot(), // ToastrModule added
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslationApiLoader,
      },
    }),
    AppRoutingModule,

    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
  ],
  declarations: [AppComponent],
  providers: [
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: (_: TranslateService, localeService: LocaleService) => localeService.getLocale(),
      deps: [TranslateService, LocaleService],
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false } satisfies OverlayDefaultConfig,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: (overlay: Overlay, inputModality: InputModalityDetector) => ({
        scrollStrategy: overlay.scrollStrategies.noop(),
        // WCAG 2.4.3 — modality-aware initial focus (getter re-evaluated per open()):
        // keyboard/screen-reader → dialog heading; mouse/touch → dialog container.
        get autoFocus() {
          return inputModality.mostRecentModality === 'keyboard' ? 'first-heading' : 'dialog';
        },
        restoreFocus: true,
      }),
      deps: [Overlay, InputModalityDetector],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' } as MatFormFieldDefaultOptions,
    },
    {
      provide: DateAdapter,
      useClass: LocaleDateAdapter,
    },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: OverlayContainer,
      useExisting: AppCdkOverlayContainer,
    },
    {
      provide: ViewportRuler,
      useExisting: AppCdkViewportRuler,
    },
    {
      provide: 'AngularJsUserService',
      useFactory: (i: any) => i.get('angularJsService'),
      deps: ['$injector'],
    },
    provideAppInitializer(() => inject(EnvironmentConfigService).loadConfig()),
    provideAppInitializer(() => inject(TranslationService).registerSignalRHandler()),
    provideHighcharts(),
    provideMarkdown(),
    { provide: TitleStrategy, useClass: CipoTitleStrategy },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule implements DoBootstrap {
  constructor(public upgrade: UpgradeModule) {}

  ngDoBootstrap() {
    // Bootstrap the hybrid application
    this.upgrade.bootstrap(document.body, ['cipo'], { strictDi: true });
  }
}
